import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, HUB_META, formatRelative } from "../lib/api";
import AppShell from "../components/AppShell";
import { useAuth } from "../lib/auth";
import { VideoCamera, Users, CalendarBlank, Plus, Broadcast, Record } from "@phosphor-icons/react";

function StatusBadge({ status }) {
  if (status === "live") return (
    <span className="label-mono flex items-center gap-1.5 px-2 py-1 border border-[#E60012] text-white bg-[#E60012]">
      <span className="hub-dot pulse bg-white" style={{ width: 6, height: 6, background: "#fff" }} /> LIVE NOW
    </span>
  );
  if (status === "ended") return (
    <span className="label-mono px-2 py-1 border border-[#27272A] text-zinc-400">ENDED</span>
  );
  return <span className="label-mono px-2 py-1 border border-[#27272A]" style={{ color: "#00E5FF" }}>SCHEDULED</span>;
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function LiveList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [filter, setFilter] = useState("all");
  const [sessions, setSessions] = useState(null);

  useEffect(() => {
    const q = filter === "all" ? "" : `?filter=${filter}`;
    api.get(`/sessions${q}`).then((r) => setSessions(r.data));
  }, [filter]);

  return (
    <AppShell>
      <div data-testid="live-list-page">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="label-mono mb-2 flex items-center gap-2" style={{ color: "#E60012" }}>
              <Broadcast size={12} weight="fill" /> LIVE SESSIONS
            </div>
            <h1 className="font-display text-white" style={{ fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 0.98 }}>
              Conversations,<br />in real time.
            </h1>
            <p className="text-zinc-400 mt-3 max-w-xl">
              Schedule a 1:1 deep-dive or a community broadcast. Recordings auto-archive for those who couldn't make it.
            </p>
          </div>
          {user && user.id && (
            <button onClick={() => nav("/live/new")} className="btn btn-primary" data-testid="live-new-btn">
              <Plus size={14} weight="bold" /> Schedule session
            </button>
          )}
        </div>

        <div className="flex gap-2 mb-6">
          {[
            { v: "all", l: "All" },
            { v: "live", l: "Live now" },
            { v: "upcoming", l: "Upcoming" },
            { v: "ended", l: "Recordings" },
          ].map((f) => (
            <button key={f.v} onClick={() => setFilter(f.v)}
              className={`btn btn-sm ${filter === f.v ? "btn-primary" : ""}`}
              data-testid={`live-filter-${f.v}`}
            >
              {f.l}
            </button>
          ))}
        </div>

        {sessions === null ? (
          <div className="py-12 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING...</div>
        ) : sessions.length === 0 ? (
          <div className="border border-dashed border-[#27272A] p-12 text-center" data-testid="live-empty">
            <VideoCamera size={32} weight="light" className="mx-auto mb-3 text-zinc-600" />
            <div className="label-mono mb-2">NO SESSIONS</div>
            <p className="text-zinc-400 text-sm mb-4">Be the first to schedule a live session in this category.</p>
            {user && user.id && <Link to="/live/new" className="btn btn-primary btn-sm">Schedule a session</Link>}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="live-grid">
            {sessions.map((s) => {
              const meta = HUB_META[s.hub_key];
              return (
                <Link key={s.id} to={`/live/${s.id}`}
                  className="card card-hover p-5 block reveal"
                  style={{ borderTop: `2px solid ${meta?.color || "#E60012"}` }}
                  data-testid={`live-card-${s.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="label-mono" style={{ color: meta?.color }}>
                      {meta?.short} · {s.hub_key.toUpperCase()}
                    </span>
                    <StatusBadge status={s.status} />
                  </div>
                  <h3 className="font-display text-xl text-white mb-2" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>{s.topic}</h3>
                  {s.description && <p className="text-zinc-400 text-sm line-clamp-2 mb-4">{s.description}</p>}
                  <div className="flex flex-wrap items-center gap-4 label-mono">
                    <span className="flex items-center gap-1.5"><CalendarBlank size={11} /> {fmtDate(s.scheduled_at)}</span>
                    <span className="flex items-center gap-1.5"><Users size={11} /> {s.participant_count}</span>
                    {s.recording_url && <span className="flex items-center gap-1.5" style={{ color: "#FFAB00" }}><Record size={11} weight="fill" /> RECORDED</span>}
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#27272A] flex items-center justify-between">
                    <span className="text-xs text-zinc-400">Host: <span className="text-white">{s.host_name}</span></span>
                    <span className="btn btn-sm" style={{ pointerEvents: "none" }}>
                      {s.status === "live" ? "Join now" : s.status === "ended" ? "View recording" : "View details"}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
